import { AsyncPipe, NgClass, NgIf, TitleCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';
import { LocaleDatePipe } from '@sc/data-access-translation';
import { dayjs } from '@sc/utils';
import { Dayjs } from 'dayjs';
import { UtilsValidators } from '../../validators';
import { IconComponent } from '../icon';
import { InputHourComponent } from '../input-hour';

@Component({
  selector: 'sc-appointment-date-picker',
  styleUrls: ['./appointment-date-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    NgClass,
    MatInputModule,
    MatDatepickerModule,
    IconComponent,
    InputHourComponent,
    NgIf,
    AsyncPipe,
    TitleCasePipe,
    TranslateModule,
    LocaleDatePipe,
  ],
  template: `
    <ng-container [formGroup]="form">
      <mat-label
        [class.asterisk]="hasRequiredValidator(start)"
        class="font-s text-grey-dark"
        >{{ 'AppointmentDatePicker.day' | translate }}</mat-label
      >
      <mat-form-field
        [ngClass]="{
          inCenter: !outsideIntervention,
          outsideIntervention: outsideIntervention
        }"
      >
        <input
          matInput
          (focus)="inputDatePicker.click()"
          (click)="inputDatePicker.click()"
          [value]="
            start?.value != null
              ? (getDateFormatted()
                | localeDate: 'fullDate':timezone
                | async
                | titlecase)
              : ''
          "
          readonly
          [disabled]="disabled"
        />

        <input
          class="hidden-input"
          #inputDatePicker
          matInput
          [value]="getStart()?.value"
          [matDatepicker]="picker"
          (focus)="picker.open()"
          (click)="picker.open()"
          (dateChange)="onDateChange($event)"
          readonly
          [disabled]="disabled"
        />
        <sc-icon matSuffix name="arrow_bottom"></sc-icon>

        <mat-datepicker
          #picker
          panelClass="appointment-date-picker-calendar"
        ></mat-datepicker>
      </mat-form-field>

      <div class="time-inputs">
        <div>
          <sc-input-hour
            formControlName="time"
            [ngClass]="{
              inCenter: !outsideIntervention,
              outsideIntervention: outsideIntervention
            }"
            [label]="'AppointmentDatePicker.start' | translate"
            [timezone]="form?.get('timezone')?.value"
            [disabled]="disabled"
          ></sc-input-hour>
        </div>
        <div>
          <sc-input-hour
            formControlName="endTime"
            [ngClass]="{
              inCenter: !outsideIntervention,
              outsideIntervention: outsideIntervention
            }"
            [label]="'AppointmentDatePicker.end' | translate"
            [timezone]="form?.get('timezone')?.value"
            [disabled]="disabled"
            [_minDate]="form.get('time')?.value"
          ></sc-input-hour>
        </div>
      </div>

      <div class="time-error" *ngIf="touched && !areTimeSelected()">
        {{ 'AppointmentDatePicker.dateAndTimeNotFilled' | translate }}
      </div>

      <div class="time-error" *ngIf="!isEndTimeValid()">
        {{ 'AppointmentDatePicker.endTimeNotValid' | translate }}
      </div>
    </ng-container>
  `,
})
export class AppointmentDatePickerComponent implements OnChanges {
  @Input() form: FormGroup;
  @Input() start: AbstractControl;
  @Input() end: AbstractControl;
  @Input() timezone: string;
  @Input() outsideIntervention = false;
  @Input() disabled = false;
  @Input() interventionDuration: number;
  @Input() addDelayToSuggestions = false;
  @Input() touched = false;

  startTime: string;
  endTime: string;

  autoAdaptTimezones = true;

  @Output() interventionDurationEvent = new EventEmitter<number>();

  constructor(private readonly changeDetectorRef: ChangeDetectorRef) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.timezone?.previousValue !== changes.timezone?.currentValue) {
      this.autoAdaptTimezones = false;
      this.onHourChange(this.startTime, 'START');
      this.onHourChange(this.endTime, 'END');
      this.autoAdaptTimezones = true;
    }
    if (changes.start?.previousValue !== changes.start?.currentValue) {
      this.start?.valueChanges.subscribe(() => {
        if (this.autoAdaptTimezones) {
          this.initTimes();
        }
      });
    }
    if (changes.end?.previousValue !== changes.end?.currentValue) {
      this.end?.valueChanges.subscribe(() => {
        if (this.autoAdaptTimezones) {
          this.initTimes();
        }
      });
    }
    this.initTimes();
  }

  initTimes() {
    if (this.getStart()?.value) {
      this.startTime = dayjs(this.getStart().value)
        .tz(this.timezone)
        ?.format('HH:mm');
    } else {
      this.startTime = null;
    }
    if (this.getEnd()?.value) {
      this.endTime = dayjs(this.getEnd().value)
        .tz(this.timezone)
        ?.format('HH:mm');
    } else {
      this.endTime = null;
    }
    this.changeDetectorRef.detectChanges();
  }

  getStart() {
    return this.start as FormControl;
  }

  getEnd() {
    return this.end as FormControl;
  }

  getDateFormatted() {
    return dayjs(this.start?.value).toISOString();
  }

  onDateChange($event) {
    const newDate = $event?.value;
    let startDateToSave: Dayjs;
    if (this.getStart().value) {
      startDateToSave = dayjs(this.getStart().value).tz(this.timezone).clone();
    } else {
      startDateToSave = dayjs().tz(this.timezone).clone();
    }
    startDateToSave = startDateToSave
      .year(newDate.year())
      .month(newDate.month())
      .date(newDate.date());
    this.start?.setValue(startDateToSave.format());

    let endDateToSave: Dayjs;
    if (this.getEnd().value) {
      endDateToSave = dayjs(this.getEnd().value).tz(this.timezone).clone();
    } else {
      endDateToSave = startDateToSave.add(
        this.interventionDuration ? this.interventionDuration : 60,
        'minute'
      );
    }
    endDateToSave = endDateToSave
      .year(newDate.year())
      .month(newDate.month())
      .date(newDate.date());
    this.end?.setValue(endDateToSave.format());
  }

  onHourChange(value: string, timeType: 'START' | 'END') {
    const { hours, minutes } = this.onHourSelection(value);

    if (hours != null && minutes != null) {
      const adjustedTime: string = this.adjustTimezoneOffsetBasedOnStartDate(
        hours,
        minutes
      );

      if (timeType === 'START') {
        this.startTime =
          String(hours).padStart(2, '0') +
          ':' +
          String(minutes).padStart(2, '0');
        this.start?.setValue(adjustedTime);
        if (this.interventionDuration) {
          this.end?.setValue(
            dayjs(adjustedTime).add(this.interventionDuration, 'minute')
          );
        }
      } else {
        this.endTime =
          String(hours).padStart(2, '0') +
          ':' +
          String(minutes).padStart(2, '0');
        this.end?.setValue(adjustedTime);
        const startDate = this.adjustTimezoneOffsetBasedOnStartDate(
          parseInt(this.startTime.substring(0, 2)),
          parseInt(this.startTime.substring(3, 5))
        );
        const diffTime = dayjs.duration(
          dayjs(adjustedTime).diff(dayjs(startDate))
        );
        const diffMinute = Math.trunc(diffTime.asMinutes());
        this.interventionDurationEvent.emit(diffMinute);
      }
    }
  }

  onHourSelection(value: string) {
    if (value) {
      let hours;
      let minutes;
      if (value.includes(':')) {
        const splitValue = value.split(':');

        hours = +splitValue[0].replace(/\D/g, '') % 24;
        minutes = +splitValue[1].replace(/\D/g, '') % 60;
      } else {
        value = value.replace(/\D/g, '');

        if (value.length > 2) {
          hours = +value.substring(0, 2) % 24;
          minutes = +value.substring(2, value.length) % 60;
        } else {
          hours = +(value ?? 0);
          minutes = 0;
        }
      }

      return { hours, minutes };
    }

    return { hours: null, minutes: null };
  }

  adjustTimezoneOffsetBasedOnStartDate(hours: number, minutes: number): string {
    const time = dayjs(this.getStart()?.value)
      .clone()
      .tz(this.timezone ?? 'Etc/UTC')
      .hour(hours)
      .minute(minutes);

    return time?.toISOString();
  }

  hasRequiredValidator(control: AbstractControl): boolean {
    return UtilsValidators.hasRequiredValidator(control);
  }

  areTimeSelected() {
    return (
      (!this.hasRequiredValidator(this.start) || this.startTime) &&
      (!this.hasRequiredValidator(this.end) || this.endTime)
    );
  }

  isEndTimeValid() {
    const startTime = dayjs(this.getStart()?.value);
    const endTime = dayjs(this.getEnd()?.value);
    return (
      !startTime.isValid() || !endTime.isValid() || startTime.isBefore(endTime)
    );
  }
}
